import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Image as ImageIcon,
    Upload,
    Youtube,
    Trash2,
    Star,
    Layers,
    Video,
} from "lucide-react";
import api, { API_BASE, errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import RepublishBanner, { notifyPendingRepublishChanged } from "@/components/RepublishBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmptyState from "@/components/EmptyState";

const CATEGORIES = [
    { code: "professional", label: "Professional", icon: ImageIcon },
    { code: "clinic", label: "Clinic", icon: ImageIcon },
    { code: "awards", label: "Awards", icon: Star },
    { code: "events", label: "Events", icon: Layers },
    { code: "videos", label: "Videos", icon: Video },
];

export default function Gallery() {
    const [doctor, setDoctor] = useState(null);
    const [items, setItems] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [activeCat, setActiveCat] = useState("professional");
    const [caption, setCaption] = useState("");
    const [youtubeUrl, setYoutubeUrl] = useState("");

    async function load() {
        try {
            const [dr, gl] = await Promise.all([
                api.get("/me/doctor"),
                api.get("/me/gallery"),
            ]);
            setDoctor(dr.data);
            setItems(gl.data || []);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load gallery"));
        }
    }
    useEffect(() => { load(); }, []);

    const grouped = useMemo(() => {
        const g = {};
        (items || []).forEach((it) => {
            const c = it?.payload?.category || "professional";
            (g[c] = g[c] || []).push(it);
        });
        return g;
    }, [items]);

    async function uploadFile(fileInput) {
        const file = fileInput.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("category", activeCat);
            fd.append("caption", caption);
            fd.append("file", file);
            const token = localStorage.getItem("ddi_token");
            const resp = await fetch(`${API_BASE}/me/gallery`, {
                method: "POST",
                body: fd,
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
                const j = await resp.json().catch(() => ({}));
                throw new Error(j.detail || `Upload failed (${resp.status})`);
            }
            toast.success("Uploaded — republish to make it live");
            setCaption("");
            fileInput.value = "";
            await load();
            notifyPendingRepublishChanged();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUploading(false);
        }
    }

    async function addYoutube() {
        if (!youtubeUrl) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("category", "videos");
            fd.append("caption", caption);
            fd.append("youtube_url", youtubeUrl);
            const token = localStorage.getItem("ddi_token");
            const resp = await fetch(`${API_BASE}/me/gallery`, {
                method: "POST",
                body: fd,
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
                const j = await resp.json().catch(() => ({}));
                throw new Error(j.detail || `Failed (${resp.status})`);
            }
            toast.success("YouTube video added — republish to make it live");
            setYoutubeUrl("");
            setCaption("");
            await load();
            notifyPendingRepublishChanged();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUploading(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Delete this gallery item?")) return;
        // Optimistic remove — put back on error.
        const prev = items;
        setItems((cur) => cur.filter((it) => it.id !== id));
        try {
            await api.delete(`/me/gallery/${id}`);
            toast.success("Deleted");
            notifyPendingRepublishChanged();
        } catch (e) {
            setItems(prev);
            toast.error(errMsg(e, "Delete failed — restored"));
        }
    }

    async function setAsProfile(id) {
        try {
            await api.post(`/me/gallery/${id}/set-as-profile`);
            toast.success("Set as profile photo");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Only images can be set as profile photo"));
        }
    }
    async function setAsCover(id) {
        try {
            await api.post(`/me/gallery/${id}/set-as-cover`);
            toast.success("Set as cover image");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Only images can be set as cover"));
        }
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="gallery-header">
                <h1 className="ddi-section-title">Media Gallery</h1>
                <p className="ddi-section-sub">
                    Upload photos and videos, organize them into categories, and pick which one becomes your profile or cover image. Nothing goes live until you republish.
                </p>
            </div>

            <RepublishBanner />

            {/* Uploader */}
            <Card className="border-[#E5E9F2] mb-6 ddi-shadow-sm">
                <CardHeader className="pb-3"><CardTitle className="text-base">Upload media</CardTitle></CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Category</label>
                            <Select value={activeCat} onValueChange={setActiveCat}>
                                <SelectTrigger data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Caption (optional)</label>
                            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Short caption shown under the image" data-testid="gallery-caption" maxLength={400} />
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl border-2 border-dashed border-[#E5E9F2] p-4 hover:border-[#3A80F7] transition-colors">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <Upload size={18} className="text-[#3A80F7]" />
                                <div>
                                    <div className="text-[13px] font-semibold text-[#0F172A]">Upload image or video</div>
                                    <div className="text-[11px] text-[#64748B]">JPG, PNG, WebP up to 5MB · MP4/WebM up to 50MB</div>
                                </div>
                                <input type="file" accept="image/*,video/*" onChange={(e) => uploadFile(e.target)} className="hidden" disabled={uploading} data-testid="gallery-file-input" />
                            </label>
                        </div>
                        <div className="rounded-xl border-2 border-dashed border-[#E5E9F2] p-4 hover:border-[#3A80F7] transition-colors">
                            <div className="flex items-center gap-3">
                                <Youtube size={18} className="text-[#DC2626]" />
                                <div className="flex-1">
                                    <div className="text-[13px] font-semibold text-[#0F172A]">Add YouTube video</div>
                                    <div className="text-[11px] text-[#64748B] mb-2">Paste an HTTPS YouTube URL</div>
                                    <div className="flex gap-2">
                                        <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="text-[13px]" data-testid="gallery-youtube-input" />
                                        <Button onClick={addYoutube} disabled={uploading || !youtubeUrl} className="bg-[#3A80F7] hover:bg-[#2D6BE0] shrink-0" data-testid="gallery-youtube-add">Add</Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Items grouped by category */}
            {CATEGORIES.map((c) => {
                const list = grouped[c.code] || [];
                if (list.length === 0) return null;
                return (
                    <Card key={c.code} className="border-[#E5E9F2] mb-5 ddi-shadow-sm" data-testid={`gallery-cat-${c.code}`}>
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-base">{c.label} <span className="text-[#94A3B8] font-normal text-[13px]">· {list.length}</span></CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {list.map((it) => {
                                    const p = it.payload || {};
                                    return (
                                        <div key={it.id} className="rounded-xl border border-[#E5E9F2] overflow-hidden bg-white hover:border-[#3A80F7] transition-colors" data-testid={`gallery-item-${it.id}`}>
                                            <div className="aspect-video bg-[#0F172A]/5 overflow-hidden">
                                                {p.media_kind === "youtube" ? (
                                                    <div className="w-full h-full flex items-center justify-center text-[#DC2626]"><Youtube size={40} /></div>
                                                ) : p.media_kind === "video" ? (
                                                    <video src={p.url ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : ""} className="w-full h-full object-cover" muted />
                                                ) : p.url ? (
                                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt={p.caption || ""} className="w-full h-full object-cover" />
                                                ) : null}
                                            </div>
                                            <div className="p-3">
                                                <div className="text-[12px] text-[#0F172A] truncate">{p.caption || <span className="text-[#94A3B8] italic">No caption</span>}</div>
                                                <div className="mt-2 flex items-center gap-1">
                                                    {p.media_kind === "image" && (
                                                        <>
                                                            <button onClick={() => setAsProfile(it.id)} className="text-[11px] text-[#3A80F7] hover:underline" data-testid={`set-profile-${it.id}`}>Set profile</button>
                                                            <span className="text-[#94A3B8]">·</span>
                                                            <button onClick={() => setAsCover(it.id)} className="text-[11px] text-[#3A80F7] hover:underline" data-testid={`set-cover-${it.id}`}>Set cover</button>
                                                        </>
                                                    )}
                                                    <button onClick={() => del(it.id)} className="ml-auto text-[11px] text-[#DC2626] hover:underline flex items-center gap-1" data-testid={`del-gallery-${it.id}`}>
                                                        <Trash2 size={11} /> Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}

            {items.length === 0 && (
                <EmptyState
                    icon={ImageIcon}
                    title="Your gallery is empty"
                    subtitle="Upload your first image or paste a YouTube URL above. Media appears on your public profile once you publish (or republish)."
                    testId="gallery-empty"
                />
            )}
        </DoctorShell>
    );
}
